import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ThemeBackground from '../Layout/ThemeBackground';
import {
  Box,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Grid,
  Snackbar,
  Alert,
  MenuItem
} from '@mui/material';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notes-tabpanel-${index}`}
      aria-labelledby={`notes-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface SessionNote {
  _id: string;
  sessionDate: string;
  attended: boolean | string;
  absenceReason?: string;
  notes: string;
  nextSessionDate: string;
  sessionNumber: number;
  counsellorName: string;
  noteType?: string;
  appointmentId?: string;
}

interface PrivateNote {
  notes: string;
  updatedAt: string;
}

const SessionNotesContainer: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [client, setClient] = useState<any>(null);
  const [tabValue, setTabValue] = useState(0);
  const [sessionNotes, setSessionNotes] = useState<SessionNote[]>([]);
  const [, setPrivateNote] = useState<PrivateNote | null>(null);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [isAssignedCounsellor, setIsAssignedCounsellor] = useState(false);
  const [newNote, setNewNote] = useState<{
    sessionDate: Date;
    attended: boolean;
    absenceReason: string;
    notes: string;
    nextSessionDate: Date | null;
    markForArchive: boolean;
  }>({
    sessionDate: new Date(),
    attended: true,
    absenceReason: '',
    notes: '',
    nextSessionDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days in future
    markForArchive: false,
  });
  const [privateNoteText, setPrivateNoteText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });
  const [showContinuationPrompt, setShowContinuationPrompt] = useState(false);
  const [isFlaggingContinuation, setIsFlaggingContinuation] = useState(false);
  const [selectedNote, setSelectedNote] = useState<SessionNote | null>(null);
  const [isViewingNote, setIsViewingNote] = useState(false);
  const [appointments, setAppointments] = useState<any[]>([]);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string>('');
  const [completeReview, setCompleteReview] = useState(false);
  const [isFirstSession, setIsFirstSession] = useState(false);
  // CRITICAL: counsellorProfileId is the Counsellor document _id (from Counsellor collection)
  // This is different from user._id which is the User document _id (from User collection)
  // Appointments and SessionNotes require the Counsellor document ID, not the User ID
  const [counsellorProfileId, setCounsellorProfileId] = useState<string>('');
  const [historicalNotesEligible, setHistoricalNotesEligible] = useState(false);
  const [historicalNotesDeadline, setHistoricalNotesDeadline] = useState<Date | null>(null);

  const checkHistoricalNotesEligibility = useCallback(async () => {
    if (user?.role !== 'counsellor') return;
    
    try {
      const response = await api.get(`/api/historical-notes/clients/${clientId}/eligibility`);
      if (response.data.eligible) {
        setHistoricalNotesEligible(true);
        if (response.data.deadline) {
          setHistoricalNotesDeadline(new Date(response.data.deadline));
        }
      }
    } catch (error) {
      // Silently fail - not eligible or error
      setHistoricalNotesEligible(false);
    }
  }, [clientId, user]);

  const fetchClientDetails = useCallback(async () => {
    try {
      const response = await api.get(`/api/clients/${clientId}`);
      setClient(response.data);
      
      // Check if this is an existing client (first session requires review)
      setIsFirstSession(response.data.isExistingClient && !response.data.hasHadSessions);
      
      // Check if current user is the assigned counsellor or an admin
      if (user?.role === 'administrator') {
        // Admins can always add session notes
        setIsAssignedCounsellor(true);
      } else if (user?.role === 'counsellor') {
        // CRITICAL: Fetch the Counsellor document ID (not the User document ID)
        // user._id = User collection document ID
        // profileResponse.data._id = Counsellor collection document ID
        // We need the Counsellor ID for appointments and session notes
        const profileResponse = await api.get('/api/counsellors/profile');
        const counsellorId = profileResponse.data._id; // This is the Counsellor document _id
        setCounsellorProfileId(counsellorId); // Store Counsellor ID for creating appointments
        const isCounsellorAssigned = response.data.currentCounsellorId === counsellorId;
        setIsAssignedCounsellor(isCounsellorAssigned);
      } else {
        setIsAssignedCounsellor(false);
      }
    } catch (error) {
      console.error('Error fetching client details:', error);
      setIsAssignedCounsellor(false);
    }
  }, [clientId, user]);

  const fetchSessionNotes = useCallback(async () => {
    try {
      const response = await api.get(`/api/clients/${clientId}/sessions`);
      console.log('=== RECEIVED SESSION NOTES ===');
      console.log('Raw response:', response.data);
      setSessionNotes(response.data);
    } catch (error) {
      console.error('Error fetching session notes:', error);
    }
  }, [clientId]);

  const fetchAppointments = useCallback(async () => {
    try {
      const response = await api.get(`/api/appointments/client/${clientId}`);
      // Only appointments that could still receive a note are offered:
      // active bookings (scheduled/rescheduled variants, past or future)
      // and 'completed' rows for retroactive entry. Every cancelled
      // variant and no_show is excluded, as is any appointment already
      // linked to a note (linkedSessionNoteId here; notedAppointmentIds
      // is the render-time fallback for rows missing the link).
      const availableAppointments = response.data.filter(
        (apt: any) =>
          !apt.linkedSessionNoteId &&
          ['scheduled', 'rescheduled', 'late_rescheduled', 'completed'].includes(apt.status)
      ).sort((a: any, b: any) => {
        // Sort by date descending (most recent first)
        return new Date(b.scheduledStart).getTime() - new Date(a.scheduledStart).getTime();
      });
      console.log('[SessionNotesContainer] Available appointments for notes:', availableAppointments);
      setAppointments(availableAppointments);
      // Only one appointment can be active at a time - auto-select it so the
      // counsellor doesn't have to pick, and derive the session date/time
      // from it (the picker is hidden in the dialog)
      if (availableAppointments.length === 1) {
        const apt = availableAppointments[0];
        setSelectedAppointmentId(apt._id);
        const sessionDate = new Date(apt.scheduledStart);
        setNewNote(prev => ({
          ...prev,
          sessionDate,
          nextSessionDate: new Date(sessionDate.getTime() + 7 * 24 * 60 * 60 * 1000)
        }));
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  }, [clientId]);

  const fetchPrivateNotes = useCallback(async () => {
    try {
      // Only fetch private notes if user is assigned counsellor
      if (isAssignedCounsellor) {
        const response = await api.get(`/api/clients/${clientId}/private-notes`);
        setPrivateNote(response.data);
        setPrivateNoteText(response.data?.notes || '');
      }
    } catch (error) {
      console.error('Error fetching private notes:', error);
    }
  }, [clientId, isAssignedCounsellor]);

  useEffect(() => {
    fetchClientDetails();
    fetchSessionNotes();
    fetchAppointments();
    fetchPrivateNotes();
    checkHistoricalNotesEligibility();
  }, [fetchClientDetails, fetchSessionNotes, fetchAppointments, fetchPrivateNotes, checkHistoricalNotesEligibility]);

  useEffect(() => {
    if (isAssignedCounsellor) {
      fetchPrivateNotes();
    }
  }, [isAssignedCounsellor, fetchPrivateNotes]);

  // An appointment that already has a session note must never be offered
  // again - every note needs its own appointment. linkedSessionNoteId
  // covers the normal case; this set is the fallback for rows where the
  // link was never persisted (e.g. a no-show whose status update failed).
  const notedAppointmentIds = new Set(
    (Array.isArray(sessionNotes) ? sessionNotes : [])
      .map((note: any) => {
        const aptId = note?.appointmentId?._id ?? note?.appointmentId;
        return aptId ? aptId.toString() : null;
      })
      .filter(Boolean) as string[]
  );

  const noteableAppointments = appointments.filter(
    (apt: any) => !notedAppointmentIds.has(apt._id?.toString())
  );

  const handleAddNote = () => {
    // Auto-select the single active appointment (dialog close resets the
    // selection, so re-apply on open)
    if (noteableAppointments.length === 1) {
      const apt = noteableAppointments[0];
      setSelectedAppointmentId(apt._id);
      const sessionDate = new Date(apt.scheduledStart);
      setNewNote(prev => ({
        ...prev,
        sessionDate,
        nextSessionDate: new Date(sessionDate.getTime() + 7 * 24 * 60 * 60 * 1000)
      }));
    }
    setIsAddingNote(true);
  };

  const handleCloseDialog = () => {
    setIsAddingNote(false);
    setSelectedAppointmentId('');
    setCompleteReview(false);
    setNewNote({
      sessionDate: new Date(),
      attended: true,
      absenceReason: '',
      notes: '',
      nextSessionDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days in future
      markForArchive: false,
    });
  };

  const handleSaveNote = async () => {
    try {
      // Check if no appointments are available
      if (noteableAppointments.length === 0) {
        setSnackbar({
          open: true,
          message: 'No appointment is available for a new note. Please book the next session from the Appointments dashboard first.',
          severity: 'error'
        });
        return;
      }

      if (!selectedAppointmentId) {
        setSnackbar({
          open: true,
          message: 'Please select an appointment for this session note.',
          severity: 'error'
        });
        return;
      }

      const appointmentId = selectedAppointmentId;

      // Validate session note data
      if (!newNote.notes.trim()) {
        setSnackbar({
          open: true,
          message: 'No appointment selected. Please select the appointment for this session.',
          severity: 'error'
        });
        return;
      }

      // Validate that session date is not in the future
      const now = new Date();
      if (newNote.sessionDate > now) {
        setSnackbar({
          open: true,
          message: 'Session date cannot be in the future',
          severity: 'warning'
        });
        return;
      }
      
      // Validate that either next session date is provided OR client is
      // flagged for admin review. A no-show is exempt - the counsellor may
      // not have been in touch with the client, so they cannot commit to a
      // date; the next appointment is booked later from the Appointments
      // dashboard.
      if (newNote.attended && !newNote.markForArchive && !newNote.nextSessionDate) {
        setSnackbar({
          open: true,
          message: 'Please either schedule a next session or flag the client for admin review',
          severity: 'warning'
        });
        return;
      }
      
      // TEMPORARILY DISABLED: Allow past dates for historic session entry
      // Validate that next session date is in the future (if provided)
      // if (newNote.nextSessionDate && newNote.nextSessionDate <= now) {
      //   setSnackbar({
      //     open: true,
      //     message: 'Next session date must be in the future',
      //     severity: 'warning'
      //   });
      //   return;
      // }
      
      // Validate Complete Review for first session of existing clients
      if (isFirstSession && !completeReview) {
        setSnackbar({
          open: true,
          message: 'Complete Review is required for the first session with an existing client',
          severity: 'error'
        });
        return;
      }
      
      setIsSaving(true);
      
      const payload = {
        appointmentId: appointmentId,
        noteType: 'session_note',
        sessionDate: newNote.sessionDate.toISOString(),
        nextSessionDate: newNote.nextSessionDate?.toISOString(),
        nextSessionSuggestedDate: newNote.nextSessionDate?.toISOString(),
        attended: newNote.attended,
        absenceReason: newNote.absenceReason,
        notes: newNote.notes,
        completeReview: completeReview
      };
      
      console.log('Saving session note with payload:', payload);
      console.log('appointmentId:', appointmentId);
      console.log('selectedAppointmentId:', selectedAppointmentId);
      
      await api.post(`/api/clients/${clientId}/sessions`, payload);

      // Handle archiving or next appointment
      if (newNote.markForArchive) {
        // Mark client for archive - flag for admin review
        try {
          // Phase 6 (decision 6): express the action - the server owns the
          // resulting pending_archive status.
          await api.put(`/api/clients/${clientId}`, {
            action: 'mark_for_archive',
            archiveRequestedBy: user?._id,
            archiveRequestedAt: new Date().toISOString()
          });
          setSnackbar({
            open: true,
            message: 'Client flagged for admin review. Admin will contact client to discuss re-allocation or archiving.',
            severity: 'info'
          });
        } catch (error: any) {
          console.error('Error marking client for archive:', error);
          setSnackbar({
            open: true,
            message: 'Session note saved, but failed to mark client for archive',
            severity: 'warning'
          });
        }
      } else if (newNote.nextSessionDate) {
        // Create next appointment if nextSessionDate is provided
        try {
          // Check for overlapping appointments
          const appointmentsResponse = await api.get(`/api/appointments/counsellor/me`);
          const existingAppointments = appointmentsResponse.data || [];
          
          const nextStart = newNote.nextSessionDate;
          const nextEnd = new Date(nextStart.getTime() + 50 * 60000);
          
          const hasOverlap = existingAppointments.some((apt: any) => {
            const aptStart = new Date(apt.scheduledStart);
            const aptEnd = new Date(apt.scheduledEnd);
            return (
              (nextStart >= aptStart && nextStart < aptEnd) ||
              (nextEnd > aptStart && nextEnd <= aptEnd) ||
              (nextStart <= aptStart && nextEnd >= aptEnd)
            );
          });

          if (hasOverlap) {
            setSnackbar({
              open: true,
              message: 'Warning: Next session time conflicts with an existing appointment',
              severity: 'warning'
            });
          } else {
            // Create the next appointment
            // CRITICAL: counsellorId must be the Counsellor document ID, not User ID
            // - For counsellors: use counsellorProfileId (Counsellor collection _id)
            // - For admins: fallback to user._id (admins don't have Counsellor profiles)
            await api.post('/api/appointments', {
              clientId,
              counsellorId: counsellorProfileId || user?._id,
              scheduledStart: nextStart.toISOString(),
              scheduledEnd: nextEnd.toISOString(),
              status: 'scheduled',
              reminderStatus: 'pending'
            });
          }
        } catch (error: any) {
          console.error('Error creating next appointment:', error);
          setSnackbar({
            open: true,
            message: 'Session note saved, but failed to create next appointment',
            severity: 'warning'
          });
        }
      }

      await fetchSessionNotes();
      await fetchClientDetails();
      await fetchAppointments();
      handleCloseDialog();

      // Check if this was the final session for an org client
      if (
        newNote.attended &&
        client?.referralCategory === 'organisation' &&
        client?.numberOfSessions &&
        (client?.completedSessions || 0) + 1 >= client.numberOfSessions
      ) {
        setShowContinuationPrompt(true);
      }
    } catch (error: any) {
      console.error('Error saving session note:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to save session note';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleContinuationResponse = async (wantsToContinue: boolean) => {
    try {
      setIsFlaggingContinuation(true);
      await api.post(`/api/clients/${clientId}/continuation-request`, {
        wantsToContinue
      });
      setShowContinuationPrompt(false);
      if (wantsToContinue) {
        setSnackbar({
          open: true,
          message: 'Admin has been notified that the client wishes to continue self-pay. They will handle the recontracting process.',
          severity: 'info'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Admin has been notified. They will handle the offboarding process.',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error('Error flagging continuation:', error);
      setSnackbar({
        open: true,
        message: 'Failed to notify admin. Please inform them manually.',
        severity: 'error'
      });
    } finally {
      setIsFlaggingContinuation(false);
    }
  };

  const handleSavePrivateNote = async () => {
    try {
      setIsSaving(true);
      await api.post(`/api/clients/${clientId}/private-notes`, {
        notes: privateNoteText
      });
      await fetchPrivateNotes();
    } catch (error) {
      console.error('Error saving private note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ThemeBackground variant="full">
      <div className="container mx-auto px-2 sm:px-4 md:px-6 py-4 sm:py-6 md:py-8 overflow-y-scroll">
        <div className="glass-card p-3 sm:p-4 md:p-6 min-h-[calc(100vh-4rem)]">
          <div className="max-w-7xl mx-auto py-3 sm:py-4 md:py-6 px-2 sm:px-4 md:px-6 lg:px-8">
            {/* Header Section */}
            <div className="glass-card-light mb-4 sm:mb-6">
              <div className="max-w-7xl mx-auto py-3 sm:py-4 md:py-6 px-2 sm:px-4 md:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  {/* Buttons on the left */}
                  <div className="flex flex-col space-y-2 sm:space-y-4 w-full sm:w-auto">
                    {/* Primary Actions */}
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                      <Button
                        variant="contained"
                        onClick={() => navigate(`/client-details/${clientId}`)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          minWidth: { xs: 'auto', sm: '160px' },
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          px: { xs: 2, sm: 3 },
                          py: { xs: 1, sm: 1.5 },
                          backgroundColor: '#2196f3',
                          '&:hover': {
                            backgroundColor: '#1976d2'
                          }
                        }}
                      >
                        Back to Client
                      </Button>
                    </div>
                  </div>

                  {/* Title on the right */}
                  <div className="text-left sm:text-right w-full sm:w-auto">
                    <Typography 
                      variant="h3" 
                      component="h1" 
                      gutterBottom
                      className="theme-text-primary"
                      sx={{
                        fontFamily: 'Roboto',
                        fontWeight: 800,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        letterSpacing: '0.5px'
                      }}
                    >
                      Session Notes
                    </Typography>
                    {client && (
                      <Typography 
                        variant="subtitle1" 
                        className="theme-text-primary"
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: '0.875rem', sm: '1rem', md: '1.1rem' }
                        }}
                      >
                        {client.firstName} {client.lastName}
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Rest of the content */}
            <Box sx={{ width: '100%', mt: 4 }}>
              <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.2)' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={(_, newValue) => setTabValue(newValue)}
                  sx={{
                    '& .MuiTab-root': {
                      color: 'rgba(255, 255, 255, 0.6)',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      '&.Mui-selected': {
                        color: 'rgba(255, 255, 255, 0.95)'
                      }
                    },
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#667eea',
                      height: '3px'
                    }
                  }}
                >
                  <Tab label="Session Notes" />
                  {isAssignedCounsellor && user?.role === 'counsellor' && <Tab label="Private Notes" />}
                </Tabs>
              </Box>
              <TabPanel value={tabValue} index={0}>
                {/* Historical Notes Banner for Counsellors */}
                {historicalNotesEligible && user?.role === 'counsellor' && (
                  <Alert 
                    severity="info" 
                    sx={{ mb: 3 }}
                    action={
                      <Button 
                        color="inherit" 
                        size="small"
                        onClick={() => navigate(`/counsellor/clients/${clientId}/historical-notes`)}
                        sx={{ fontWeight: 600 }}
                      >
                        Complete Now
                      </Button>
                    }
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      📝 Historical Session Notes Available
                    </Typography>
                    <Typography variant="body2">
                      You have been invited to add historical session notes for this client.
                      {historicalNotesDeadline && (
                        <> Deadline: {historicalNotesDeadline.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</>
                      )}
                    </Typography>
                  </Alert>
                )}
                
                {/* Hide Add Session Note button for pending_archive clients and
                    until an appointment exists - a note always belongs to one.
                    When none is available, tell the counsellor what to do:
                    book the next session first, then add the note. */}
                {client?.status !== 'pending_archive' && noteableAppointments.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Button
                      onClick={handleAddNote}
                      variant="contained"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: { xs: 'auto', sm: '160px' },
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1.5 },
                        backgroundColor: '#2196f3',
                        '&:hover': {
                          backgroundColor: '#1976d2'
                        }
                      }}
                    >
                      Add Session Note
                    </Button>
                  </Box>
                )}
                {client?.status !== 'pending_archive' && isAssignedCounsellor && noteableAppointments.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(33, 150, 243, 0.08)' }}>
                    <Typography variant="body2" className="theme-text-primary">
                      No appointment is available for a new note. Each session note must be linked to its own appointment - book the next session from the Appointments dashboard first, then add the note here.
                    </Typography>
                  </Alert>
                )}

                <Grid container spacing={3}>
                  {Array.isArray(sessionNotes) && sessionNotes.map((note) => {
                    console.log('Rendering card for note:', note._id);
                    console.log('  sessionDate:', note.sessionDate);
                    console.log('  attended:', note.attended);
                    console.log('  counsellorName:', note.counsellorName);
                    return (
                    <Grid item xs={12} sm={6} md={4} key={note._id}>
                      <Box 
                        sx={{ 
                          p: 2.5,
                          background: 'rgba(255, 255, 255, 0.05)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '12px',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.3)',
                            borderColor: 'rgba(102, 126, 234, 0.4)'
                          }
                        }}
                      >
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" className="theme-text-primary" sx={{ fontWeight: 700, fontSize: '1.1rem', mb: 1 }}>
                            Session #{note.sessionNumber}
                          </Typography>
                          <Typography variant="body2" className="theme-text-secondary" sx={{ fontSize: '0.85rem' }}>
                            {note.sessionDate ? new Date(note.sessionDate).toLocaleString('en-GB', { 
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            }) : 'Not set'}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 2, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box 
                              sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%',
                                bgcolor: (() => {
                                  if (note.attended === true || note.attended === 'true') return '#4caf50';
                                  if (note.attended === false || note.attended === 'false') return '#f44336';
                                  return note.attended !== undefined && note.attended !== null ? '#4caf50' : '#f44336';
                                })(),
                                mr: 1
                              }}
                            />
                            <Typography variant="body2" className="theme-text-primary" sx={{ fontSize: '0.9rem', fontWeight: 600 }}>
                              {(() => {
                                if (note.attended === true || note.attended === 'true') return 'Attended';
                                if (note.attended === false || note.attended === 'false') return 'Did Not Attend';
                                return note.attended !== undefined && note.attended !== null ? 'Attended' : 'Did Not Attend';
                              })()}
                            </Typography>
                          </Box>
                          
                          <Typography 
                            variant="body2" 
                            className="theme-text-secondary" 
                            sx={{ 
                              fontSize: '0.85rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {note.notes}
                          </Typography>
                        </Box>

                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setSelectedNote(note);
                            setIsViewingNote(true);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: 'rgba(102, 126, 234, 0.5)',
                            color: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                              borderColor: '#667eea',
                              backgroundColor: 'rgba(102, 126, 234, 0.1)'
                            }
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </Grid>
                    );
                  })}
                </Grid>
              </TabPanel>

              {isAssignedCounsellor && (
                <TabPanel value={tabValue} index={1}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" className="theme-text-primary" sx={{ mb: 2, fontWeight: 600 }}>
                      Private Notes (Only visible to you)
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      value={privateNoteText}
                      onChange={(e) => setPrivateNoteText(e.target.value)}
                      placeholder="Add your private notes here..."
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: 'rgba(255, 255, 255, 0.9)',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.2)'
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3)'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#667eea'
                          }
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'rgba(255, 255, 255, 0.4)',
                          opacity: 1
                        }
                      }}
                    />
                    <Button
                      onClick={handleSavePrivateNote}
                      variant="contained"
                      disabled={isSaving}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        minWidth: '160px',
                        backgroundColor: '#4caf50',
                        '&:hover': {
                          backgroundColor: '#388e3c'
                        },
                        mt: 2
                      }}
                    >
                      Save Private Notes
                    </Button>
                  </Box>
                </TabPanel>
              )}
            </Box>
          </div>
        </div>
      </div>

      <Dialog 
        open={isAddingNote} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography 
            variant="h6" 
            className="theme-text-primary"
            sx={{ 
              fontFamily: 'Roboto', 
              fontWeight: 600,
              fontSize: '1.25rem'
            }}
          >
            Add Session Note
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <div style={{ marginBottom: '8px' }}>
                {noteableAppointments.length === 0 ? (
                  // No scheduled appointments - show message
                  <Alert severity="warning" sx={{ backgroundColor: 'rgba(255, 152, 0, 0.1)' }}>
                    <Typography variant="body2" className="theme-text-primary">
                      No appointment is available for a new note. Each session note must be linked to its own appointment - book the next session from the Appointments dashboard first.
                    </Typography>
                  </Alert>
                ) : noteableAppointments.length === 1 ? (
                  // Only one appointment can be active at a time - it is
                  // auto-selected, so there is nothing to pick. Show which
                  // appointment this note is linked to instead of a dropdown.
                  <Typography variant="body2" className="theme-text-muted">
                    Session linked to appointment:{' '}
                    {new Date(noteableAppointments[0].scheduledStart).toLocaleString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                ) : (
                  // Edge case: more than one note-less appointment exists -
                  // counsellor must pick which one this note belongs to
                  <>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">Appointment *</label>
                  <TextField
                    select
                    fullWidth
                    value={selectedAppointmentId}
                    onChange={(e) => {
                      setSelectedAppointmentId(e.target.value);
                      // Auto-fill session date from appointment and set next session to 7 days later
                      const selectedApt = noteableAppointments.find(apt => apt._id === e.target.value);
                      if (selectedApt) {
                        const sessionDate = new Date(selectedApt.scheduledStart);
                        const nextSessionDate = new Date(sessionDate.getTime() + 7 * 24 * 60 * 60 * 1000);
                        setNewNote({ 
                          ...newNote, 
                          sessionDate,
                          nextSessionDate
                        });
                      }
                    }}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'rgba(255, 255, 255, 0.9)',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)'
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea'
                        }
                      },
                      '& .MuiSelect-select': {
                        color: 'rgba(255, 255, 255, 0.9)'
                      }
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            bgcolor: 'rgba(30, 30, 46, 0.98)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            '& .MuiMenuItem-root': {
                              color: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                              },
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(102, 126, 234, 0.3)',
                                '&:hover': {
                                  backgroundColor: 'rgba(102, 126, 234, 0.4)'
                                }
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value="">-- Select the appointment for this session --</MenuItem>
                    {noteableAppointments.map((apt) => (
                      <MenuItem key={apt._id} value={apt._id}>
                        {new Date(apt.scheduledStart).toLocaleString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </MenuItem>
                    ))}
                  </TextField>
                  </>
                )}
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className="mb-6" style={{ position: 'relative', zIndex: 1000 }}>
                <label className="block text-sm font-medium theme-text-secondary mb-2">Actual Session Date / Time *</label>
                <DatePicker
                  selected={newNote.sessionDate}
                  onChange={(date: Date | null) => {
                    if (date) {
                      setNewNote({ ...newNote, sessionDate: date });
                    }
                  }}
                  showTimeSelect
                  dateFormat="dd/MM/yyyy HH:mm"
                  className="mt-1 block w-full p-3 border border-white/20 rounded-lg bg-white/5 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 transition-all"
                  placeholderText="Select date and time"
                  portalId="root"
                />
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className="mb-6" style={{ position: 'relative', zIndex: 1000 }}>
                <label className="block text-sm font-medium theme-text-secondary mb-2">
                  Next Session Date / Time {newNote.attended && !newNote.markForArchive && <span className="text-red-400">*</span>}
                </label>
                <DatePicker
                  selected={newNote.nextSessionDate}
                  onChange={(date: Date | null) => {
                    if (date) {
                      setNewNote({ ...newNote, nextSessionDate: date, markForArchive: false });
                    }
                  }}
                  showTimeSelect
                  dateFormat="dd/MM/yyyy HH:mm"
                  className="mt-1 block w-full p-3 border border-white/20 rounded-lg bg-white/5 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/50 transition-all"
                  placeholderText={newNote.markForArchive ? "Not scheduling (flagged for admin review)" : "Select date and time"}
                  portalId="root"
                  disabled={newNote.markForArchive}
                />
                {!newNote.markForArchive && (
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', mt: 0.5, display: 'block' }}>
                    {newNote.attended
                      ? 'Required unless flagging for admin review'
                      : 'Optional for a no-show - book the next appointment once the client is in touch'}
                  </Typography>
                )}
              </div>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newNote.attended}
                    onChange={(e) => {
                      const attended = e.target.checked;
                      setNewNote({
                        ...newNote,
                        attended,
                        // A no-show can't promise a date without contact -
                        // clear the auto-suggested +7d default so no phantom
                        // appointment is created; restore it if attendance
                        // is re-ticked.
                        nextSessionDate: attended
                          ? (newNote.nextSessionDate ?? new Date(newNote.sessionDate.getTime() + 7 * 24 * 60 * 60 * 1000))
                          : null
                      });
                    }}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      '&.Mui-checked': {
                        color: '#667eea'
                      }
                    }}
                  />
                }
                label={
                  <Typography className="theme-text-primary" sx={{ fontSize: '0.875rem' }}>
                    Client Attended
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={newNote.markForArchive}
                    onChange={(e) => setNewNote({ 
                      ...newNote, 
                      markForArchive: e.target.checked,
                      nextSessionDate: e.target.checked ? null : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                    })}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      '&.Mui-checked': {
                        color: '#f59e0b'
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography className="theme-text-primary" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      Flag for Admin Review (Potential Archive)
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      Check this if no future sessions are planned. Admin will contact client to discuss re-allocation or archiving.
                    </Typography>
                  </Box>
                }
              />
            </Grid>
            {!newNote.attended && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Absence Reason"
                  value={newNote.absenceReason}
                  onChange={(e) => setNewNote({ ...newNote, absenceReason: e.target.value })}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'rgba(255, 255, 255, 0.9)',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea'
                      }
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.6)'
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#667eea'
                    }
                  }}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Session Notes"
                value={newNote.notes}
                onChange={(e) => setNewNote({ ...newNote, notes: e.target.value.slice(0, 250) })}
                required
                placeholder="Enter session notes here..."
                helperText={`Session notes should be brief which is why they have been restricted to 250 characters. ${newNote.notes.length}/250`}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'rgba(255, 255, 255, 0.9)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.6)'
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#667eea'
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  },
                  '& .MuiOutlinedInput-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.4)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
            
            {/* Complete Review Checkbox */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={completeReview}
                    onChange={(e) => setCompleteReview(e.target.checked)}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.5)',
                      '&.Mui-checked': {
                        color: '#f59e0b'
                      }
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography className="theme-text-primary" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      Complete Review {isFirstSession && <span style={{ color: '#f59e0b' }}>*</span>}
                    </Typography>
                    {isFirstSession && (
                      <Typography variant="caption" sx={{ color: '#f59e0b', display: 'block' }}>
                        Required for first session with existing client
                      </Typography>
                    )}
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <div className="flex justify-end gap-3 w-full">
            <Button
              onClick={handleCloseDialog}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1,
                borderColor: 'rgba(255, 255, 255, 0.3)',
                color: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveNote}
              variant="contained"
              disabled={isSaving}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            >
              {isSaving ? 'Saving...' : 'Save Note'}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      {/* View Session Note Details Modal */}
      <Dialog
        open={isViewingNote}
        onClose={() => {
          setIsViewingNote(false);
          setSelectedNote(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography 
            variant="h6" 
            className="theme-text-primary"
            sx={{ 
              fontFamily: 'Roboto', 
              fontWeight: 600,
              fontSize: '1.25rem'
            }}
          >
            Session #{selectedNote?.sessionNumber} Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedNote && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" className="theme-text-secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Session Date:
                </Typography>
                <Typography className="theme-text-primary" sx={{ fontSize: '0.95rem' }}>
                  {selectedNote.sessionDate ? new Date(selectedNote.sessionDate).toLocaleString('en-GB', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'Not set'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" className="theme-text-secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Next Session:
                </Typography>
                <Typography className="theme-text-primary" sx={{ fontSize: '0.95rem' }}>
                  {selectedNote.nextSessionDate ? new Date(selectedNote.nextSessionDate).toLocaleString('en-GB', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : 'Not set'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" className="theme-text-secondary" sx={{ fontWeight: 600, mr: 1 }}>
                    Attended:
                  </Typography>
                  <Box 
                    sx={{ 
                      width: 10, 
                      height: 10, 
                      borderRadius: '50%',
                      bgcolor: (() => {
                        if (selectedNote.attended === true || selectedNote.attended === 'true') return '#4caf50';
                        if (selectedNote.attended === false || selectedNote.attended === 'false') return '#f44336';
                        return selectedNote.attended !== undefined && selectedNote.attended !== null ? '#4caf50' : '#f44336';
                      })(),
                      mr: 1
                    }}
                  />
                  <Typography className="theme-text-primary" sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
                    {(() => {
                      if (selectedNote.attended === true || selectedNote.attended === 'true') return 'Yes';
                      if (selectedNote.attended === false || selectedNote.attended === 'false') return 'No';
                      return selectedNote.attended !== undefined && selectedNote.attended !== null ? 'Yes' : 'No';
                    })()}
                  </Typography>
                </Box>
              </Grid>
              {(selectedNote.attended === false || selectedNote.attended === 'false') && selectedNote.absenceReason && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" className="theme-text-secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Absence Reason:
                  </Typography>
                  <Typography className="theme-text-primary" sx={{ fontSize: '0.95rem' }}>
                    {selectedNote.absenceReason}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography variant="subtitle2" className="theme-text-secondary" sx={{ fontWeight: 600, mb: 1 }}>
                  Session Notes:
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  <Typography className="theme-text-primary" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem', lineHeight: 1.6 }}>
                    {selectedNote.notes}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" className="theme-text-secondary" sx={{ fontSize: '0.75rem' }}>
                  Counsellor: {selectedNote.counsellorName}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => {
              setIsViewingNote(false);
              setSelectedNote(null);
            }}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              py: 1,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)',
                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Session Limit Continuation Prompt */}
      <Dialog
        open={showContinuationPrompt}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ color: '#f59e0b', fontWeight: 700 }}>
            Session Limit Reached
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography className="theme-text-secondary" sx={{ mb: 2 }}>
            <strong>{client?.firstName} {client?.lastName}</strong> has now completed all{' '}
            <strong>{client?.numberOfSessions}</strong> sessions allocated by{' '}
            <strong>{client?.organisationDisplayName}</strong>.
          </Typography>
          <Typography className="theme-text-primary" sx={{ fontWeight: 600, mb: 1 }}>
            Does the client wish to continue with the service on a self-pay basis?
          </Typography>
          <Typography className="theme-text-secondary" sx={{ fontSize: '0.85rem' }}>
            If yes, the admin will be notified to set up a new contract. If no, the admin will be notified to offboard the client.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, gap: 2 }}>
          <Button
            onClick={() => handleContinuationResponse(false)}
            disabled={isFlaggingContinuation}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': { borderColor: '#dc2626', color: '#dc2626' }
            }}
          >
            {isFlaggingContinuation ? 'Notifying...' : 'No — Notify Admin to Offboard'}
          </Button>
          <Button
            onClick={() => handleContinuationResponse(true)}
            disabled={isFlaggingContinuation}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              backgroundColor: '#f59e0b',
              '&:hover': { backgroundColor: '#d97706' }
            }}
          >
            {isFlaggingContinuation ? 'Notifying...' : 'Yes — Notify Admin to Recontract'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeBackground>
  );
};

export default SessionNotesContainer;
